import { ShieldCheck, Users, LineChart, Award } from "lucide-react";
import SEO from "@/components/SEO";

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About — Professional Asian Handicap Football Tipsters"
        description="Asian Picks is run by European football analysts specialising in the Asian Handicap market. Learn how our disciplined, transparent tipping model produces an 81.1% win rate over 1,114+ tips."
        path="/about"
        keywords="Asian Handicap tipster, professional football analysts, AH betting service, football syndicate tips, verified tipster"
      />
      <section className="bg-ink-950 text-white py-16 sm:py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/1657324/pexels-photo-1657324.jpeg')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/90 via-ink-950/70 to-ink-950" />
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-emerald-700/25 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gold-400 font-mono uppercase tracking-widest text-xs mb-3">
            About Us
          </div>
          <h1
            data-testid="about-title"
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl"
          >
            Built by <span className="gold-text">bettors</span>, for people who take the game seriously.
          </h1>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-ink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="font-display font-bold text-3xl text-ink-900 mb-6">
              Our story
            </h2>
            <div className="space-y-5 text-ink-700 leading-relaxed">
              <p>
                Asian Picks was founded in 2023 by a small team of European
                football analysts who had spent nearly a decade quietly beating
                Asian Handicap markets for private syndicates. We started this
                service after too many friends and colleagues asked the same
                question: "Where can I find honest, disciplined AH tips without
                paying for hype?"
              </p>
              <p>
                Most tipping services chase big-odds accumulators because they
                generate loud, screenshot-friendly wins. We don't. Our entire
                model is built around one simple, boring idea: find fair Asian
                Handicap lines in the 1.85–2.20 odds range, back them at flat
                stakes, and let variance settle over hundreds of picks.
              </p>
              <p>
                That approach isn't glamorous. It doesn't produce viral tweets
                or 20x weekend slips. It does, however, produce a{" "}
                <span className="font-semibold text-emerald-700">81.1% verified win rate</span>{" "}
                over more than 1,100 tips — and that's the record we publish
                openly in our archive so anyone can audit it.
              </p>
            </div>

            <h2 className="font-display font-bold text-3xl text-ink-900 mt-14 mb-6">
              What we believe
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
              {[
                {
                  icon: ShieldCheck,
                  title: "Transparency is the product",
                  desc: "If we can't show every past pick — win, loss, or push — we shouldn't be trusted with your bankroll.",
                },
                {
                  icon: LineChart,
                  title: "Edge lives in one market",
                  desc: "Specialisation beats breadth. The Asian Handicap is where we've spent every hour of research since day one.",
                },
                {
                  icon: Users,
                  title: "Members are partners",
                  desc: "You're not a churn number to us. Long-term subscribers get direct access to our analysts.",
                },
                {
                  icon: Award,
                  title: "Discipline > drama",
                  desc: "We'll never chase losses, upsell during a downswing, or hide a bad week. The record is the record.",
                },
              ].map((v) => (
                <div
                  key={v.title}
                  className="p-6 rounded-xl bg-white border border-ink-200"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-700/10 flex items-center justify-center mb-4">
                    <v.icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-ink-900 mb-2">
                    {v.title}
                  </h3>
                  <p className="text-sm text-ink-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="font-display font-bold text-3xl text-ink-900 mt-14 mb-6">
              What Asian Picks is not
            </h2>
            <ul className="space-y-3 text-ink-700 leading-relaxed">
              <li>• We are <span className="font-semibold">not a bookmaker</span> and do not accept wagers.</li>
              <li>• We do <span className="font-semibold">not sell "guaranteed wins"</span>. Anyone who does is lying.</li>
              <li>• We do <span className="font-semibold">not promote gambling</span> to under-18s or vulnerable users.</li>
              <li>• We do <span className="font-semibold">not push high-odds accumulators</span> — every pick sits in the 1.85–2.20 sweet spot.</li>
            </ul>

            <div className="mt-14 p-6 rounded-xl border border-emerald-700/30 bg-emerald-700/[0.04] text-emerald-900">
              <p className="text-sm leading-relaxed">
                Ready to see how we work in practice? Browse the fully public{" "}
                <a href="/predictions" className="font-semibold underline">
                  Predictions Archive
                </a>{" "}
                — every settled tip is there, sorted newest first. Judge us on
                the record, not the marketing.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
